/* eslint-disable react/no-array-index-key */
/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from 'react';
import {
  Divider,
  Stack,
  Box,
  Typography as Muitypo,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { Typography, Chip } from '../../components';
import { CustomerVehicle, ICompSummaryBase, InventoryVehicle } from '../../types';
import { useAppSelector } from '../../hooks/useRedux';
import { countryMapping } from '../../utils/constants';
import AssignmentAlert from '../Assignments/AssignmentAlert';
import { rearrangeArray } from '../../utils/arrays';

interface Props {
  customerVehicle: CustomerVehicle;
  inventoryVehicle: InventoryVehicle;
  showDifferences?: boolean;
  metaData?: Record<string, any>;
  transactionErrors?: any;
  retryCall?: any;
}

const configurationOrder: string[] = [
  'carModel',
  'model',
  'variant',
  'bundle',
  'bundleName',
  'driveTrain',
  'exteriorColor',
  'trim',
  'roof',
  'wheels',
  'interiorColor',
  'lucidFrontSeating',
  'lucidRearSeating',
  'ada',
  'soundSystem',
  'lucidComfortAndConvenience',
  'dynamicHandlingPackage',
  'technologyPackage',
  'towingPackage',
  'totalPrice',
  'vehiclePrice',
  'currentLocationName',
  'locationCurrency',
];

const compIgnoreKeys: (keyof ICompSummaryBase | string)[] = [
  'orderNumber',
  'imageUrl',
  'customerStage',
  'stage',
  'compType',
  'vin',
  '_id',
  'email',
  'reservationNumber',
  'phone',
  'vehicleStage',
  'customerStage',
  'customerName',
];

type ICombinedObj = ICompSummaryBase & CustomerVehicle & CustomerVehicle;
type ICombinedKeys = keyof ICombinedObj;
type TBundleFields = {
  bundle?: string;
  bundleName?: string;
};

const keyToCellHeading: Partial<Record<ICombinedKeys & string, string>> = {
  // currentLocationName: 'Current Location',
  model: 'Model',
  variant: 'Variant',
  bundle: 'Bundle',
  totalPrice: 'Price',
  year: 'Year',
  exteriorColor: 'Exterior Color',
  interiorColor: 'Interior Color',
  trim: 'Appearance (Trim)',
  roof: 'Roof',
  wheels: 'Wheels',
  soundSystem: 'Sound System',
  ada: 'ADAS',
  driveTrain: 'Drive Train',
  lucidComfortAndConvenience: 'Comfort & Convenience Package',
  lucidFrontSeating: 'Front Seating',
  lucidRearSeating: 'Rear Seating',
  dynamicHandlingPackage: 'Dynamic Handling Package',
  technologyPackage: 'Technology Package',
  towingPackage: 'Towing Package',
  // suspensionDamp: 'Suspension Damper',
};

const keyToCellHeadingGravity: Partial<Record<ICombinedKeys & string, string>> = {
  // currentLocationName: 'Current Location',
  model: 'Model',
  variant: 'Variant',
  bundle: 'Bundle',
  totalPrice: 'Price',
  year: 'Year',
  exteriorColor: 'Exterior Color',
  interiorColor: 'Interior Color',
  trim: 'Appearance (Trim)',
  roof: 'Roof',
  wheels: 'Wheels',
  soundSystem: 'Sound System',
  ada: 'ADAS',
  driveTrain: 'Drive Train',
  lucidComfortAndConvenience: 'Comfort & Convenience Package',
  lucidFrontSeating: 'Front Seating',
  lucidRearSeating: 'Rear Seating',
  dynamicHandlingPackage: 'Dynamic Handling Package',
  technologyPackage: 'Technology Package',
  towingPackage: 'Towing Package',
  // suspensionDamp: 'Suspension Damper',
};

const compKeys: string[] = [
  'locationCurrency',
  'lucidComfortAndConvenience',
  'lucidFrontSeating',
  'carModel',
  'bundle',
  'bundleName',
  ...Object.keys(keyToCellHeading),
];

const VehicleCompare: React.FC<Props> = ({
  customerVehicle,
  inventoryVehicle,
  showDifferences = true,
  metaData = {},
  transactionErrors,
  retryCall,
}) => {
  const [differences, setDifferences] = useState<ICombinedKeys[]>([]);
  const [propertyToVal, setPropertyToVal] = useState<
    { colNameKey: string; customerVeh: string; inventoryVeh: string }[] | null
  >(null);

  useEffect(() => {
    let uniqueKeys = Array.from(
      new Set([...Object.keys(customerVehicle || {}), ...Object.keys(inventoryVehicle)])
    ) as ICombinedKeys[];
    const diff: typeof differences = [];
    const prpToVal: typeof propertyToVal = [];

    const locationIdx = uniqueKeys.findIndex((v) => v === 'currentLocationName');
    if (locationIdx !== -1) {
      const locationVal = uniqueKeys[locationIdx];
      uniqueKeys.splice(locationIdx, 1);
      uniqueKeys = [locationVal, ...uniqueKeys];
    }
    uniqueKeys = rearrangeArray(uniqueKeys, configurationOrder);
    uniqueKeys.forEach((k: string) => {
      const normalizedKey = k === 'bundleName' ? 'bundle' : k;
      // locationCurrency is only for price formatting not for difference count
      if (compKeys.includes(k as keyof typeof keyToCellHeading)) {
        const invPrice = inventoryVehicle?.vehiclePrice?.filter(
          (a: any) => a.locale === countryMapping[customerVehicle?.deliveryCountryCode]
        )[0]?.totalPrice;
        const invCurrency = inventoryVehicle?.vehiclePrice?.filter(
          (a: any) => a.locale === countryMapping[customerVehicle?.deliveryCountryCode]
        )[0]?.currency;

        const customerTempLocation = `${customerVehicle.currentCity} ${customerVehicle.currentCity ? ', ' : ''} ${
          customerVehicle.currentState ? customerVehicle.currentState : customerVehicle.country
        }`;

        const customerLocation = !customerVehicle?.deliveryCenter
          ? customerVehicle.deliveryCountryCode
          : customerTempLocation;

        const invCountry =
          inventoryVehicle.region === 'Europe' ? inventoryVehicle.transitCountry : inventoryVehicle.country;
        const invLocation = `${inventoryVehicle.currentCity} ${inventoryVehicle.currentCity ? ', ' : ''} ${
          inventoryVehicle.currentState ? inventoryVehicle.currentState : invCountry
        }`;
        const prpVal = {
          colNameKey: normalizedKey,
          // eslint-disable-next-line no-nested-ternary
          customerVeh: customerVehicle
            ? (normalizedKey as keyof typeof keyToCellHeading) === 'currentLocationName'
              ? customerLocation
              : (normalizedKey === 'bundle'
                  ? ((customerVehicle as TBundleFields)?.bundle ||
                    (customerVehicle as TBundleFields)?.bundleName)
                  : customerVehicle[normalizedKey as keyof typeof keyToCellHeading]) || 'N/A'
            : 'N/A',
          inventoryVeh:
            // eslint-disable-next-line no-nested-ternary
            (normalizedKey as keyof InventoryVehicle) === 'totalPrice'
              ? invPrice
              : // eslint-disable-next-line no-nested-ternary
              (normalizedKey as keyof InventoryVehicle) === 'locationCurrency'
              ? invCurrency
              : (normalizedKey as keyof InventoryVehicle) === 'currentLocationName'
              ? invLocation
              : (normalizedKey === 'bundle'
                  ? inventoryVehicle.bundle || inventoryVehicle.bundleName
                  : inventoryVehicle[normalizedKey as keyof InventoryVehicle]) || 'N/A',
        };
        if (
          prpVal.customerVeh &&
          prpVal.customerVeh !== prpVal.inventoryVeh &&
          prpVal.colNameKey !== 'locationCurrency' &&
          showDifferences
        ) {
          diff.push(prpVal.colNameKey as keyof typeof keyToCellHeading);
        }
        const existingIdx = prpToVal.findIndex((entry) => entry.colNameKey === prpVal.colNameKey);
        if (existingIdx === -1) {
          prpToVal.push(prpVal);
        } else {
          prpToVal[existingIdx] = prpVal;
        }
      }
    });

    setPropertyToVal(prpToVal);
    setDifferences(diff);
  }, [inventoryVehicle, customerVehicle, showDifferences]);

  const invImg = useAppSelector((state) => {
    const formatUrl = inventoryVehicle.imageUrl;
    if (formatUrl) {
      const url = new URL(formatUrl);
      const { searchParams } = url;

      url.searchParams.set('width', '630');
      url.searchParams.set('height', '472.5');
      url.searchParams.set('format', 'png');
      // url.searchParams.set('camera', 'Angle_EXT_10_0010');
      url.searchParams.set('background_colour', '00000000');
      // url.searchParams.set('env', 'lucid_studio');

      // change the search property of the main url
      url.search = searchParams.toString();

      // the new url string
      const newUrl = url.toString();

      return newUrl;
    }
    return inventoryVehicle.imageUrl;
  });

  const img = useAppSelector((state) => {
    const formatUrl = customerVehicle?.imageUrl;
    if (formatUrl) {
      const url = new URL(formatUrl);
      const { searchParams } = url;
      if (customerVehicle?.carModel === 'Gravity') {
        url.searchParams.set('width', '3840');
        url.searchParams.set('height', '2880');
      } else {
        url.searchParams.set('width', '630');
        url.searchParams.set('height', '472.5');
      }
      url.searchParams.set('format', 'png');
      // url.searchParams.set('camera', 'Angle_EXT_10_0010');
      url.searchParams.set('background_colour', '00000000');
      // url.searchParams.set('env', 'lucid_studio');

      // change the search property of the main url
      url.search = searchParams.toString();

      // the new url string
      const newUrl = url.toString();

      return newUrl;
    }
    return customerVehicle?.imageUrl;
  });
  const currencyFormatter = (value: number, currency: string) => {
    if (Number.isNaN(value) || value === undefined || value == null) {
      return '-';
    }
    if (currency && currency.length > 0) {
      try {
        const formattedValue = new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
        return `${formattedValue}`;
      } catch (error) {
        return `${Math.abs(+value).toFixed(2)}`;
      }
    }
    return `${Math.abs(+value).toFixed(2)}`;
  };

  const customerDisplayName = () => {
    if (customerVehicle?.customerName && !customerVehicle?.customerName.includes('undefined')) {
      return customerVehicle?.customerName;
    }
    if (metaData.customerName) {
      return metaData.customerName;
    }
    return 'N/A';
  };
  return (
    <>
      {transactionErrors &&
        transactionErrors?.requestId &&
        (transactionErrors?.sfdcResponse?.data || transactionErrors?.vmsResponse?.data) && (
          <AssignmentAlert transactionErrors={transactionErrors} retryCall={retryCall} />
        )}
      {/* <AssignmentAlert transactionErrors={transactionErrors} retryCall={retryCall} /> */}
      <Stack divider={<Divider orientation="vertical" flexItem />} direction="row" gap={2} mt={1.5}>
        {differences.length > 0 && (
          <Box
            width="100%"
            sx={{
              background: '#E2EDFE',
              borderRadius: '4px',
              padding: '7px 10px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Icon icon="ri:information-line" fontSize={14} color="#E04732" />
            <Typography weight={400} size={12} color="#1F1F1F" marginLeft={1}>
              {differences.length} differences are found between vehicles. Please review before approving.
            </Typography>
          </Box>
        )}
      </Stack>
      {/* {summeryMetaData?.tabs && <ActionsTabs tabs={summeryMetaData?.tabs} />} */}
      <Grid container marginTop={1.5}>
        <Grid item xs={12} sx={{ background: '#fff' }}>
          <TableContainer>
            <Table sx={{ width: '100%' }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ borderRight: '1px solid #E3E3E3', borderBottom: '1px solid #E3E3E3', width: '24%' }}
                  />
                  <TableCell
                    sx={{
                      borderRight: '1px solid #E3E3E3',
                      borderBottom: '1px solid #E3E3E3',
                      p: (theme) => theme.spacing(3.6, 4.5, 3, 4.5),
                      width: '38%',
                    }}
                  >
                    <Muitypo
                      variant="h4"
                      sx={{
                        fontSize: '20px',
                        fontWeight: '500',
                        color: '#1C1951',
                        textAlign: 'center',
                        lineHeight: '24px',
                      }}
                    >
                      Customer Vehicle
                    </Muitypo>
                    <Stack display="flex" alignItems="center" justifyContent="center" mt={5}>
                      <Box marginBottom={5}>
                        <img src={img} alt="" width="300px" />
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={5} lg={4} sx={{ paddingTop: '5px' }}>
                          <Muitypo sx={{ fontSize: '12px', fontWeight: '400', color: '#3e3e3e', lineHeight: '2' }}>
                            Stage
                          </Muitypo>
                        </Grid>
                        <Grid item xs={7} lg={8} sx={{ paddingTop: '5px' }}>
                          <Chip text={customerVehicle?.vehicleStage || 'N/A'} />
                        </Grid>
                        <Grid item xs={5} lg={4} sx={{ paddingTop: '5px' }}>
                          <Muitypo sx={{ fontSize: '12px', fontWeight: '400', color: '#3e3e3e', lineHeight: '2' }}>
                            Batch Number
                          </Muitypo>
                        </Grid>
                        <Grid item xs={7} lg={8} sx={{ paddingTop: '5px' }}>
                          <Muitypo variant="caption">{customerVehicle?.batchNumber || 'N/A'}</Muitypo>
                        </Grid>
                        <Grid item xs={5} lg={4} sx={{ paddingTop: '5px' }}>
                          <Muitypo sx={{ fontSize: '12px', fontWeight: '400', color: '#3e3e3e', lineHeight: '2' }}>
                            VIN
                          </Muitypo>
                        </Grid>
                        <Grid item xs={7} lg={8} sx={{ paddingTop: '5px' }}>
                          <Muitypo variant="caption">{customerVehicle?.vin || 'N/A'}</Muitypo>
                        </Grid>
                        <Grid item xs={5} lg={4} sx={{ paddingTop: '5px' }}>
                          <Muitypo sx={{ fontSize: '12px', fontWeight: '400', color: '#3e3e3e', lineHeight: '2' }}>
                            Customer Stage
                          </Muitypo>
                        </Grid>
                        <Grid item xs={7} lg={8} sx={{ paddingTop: '5px' }}>
                          <Chip text={customerVehicle?.customerStage || 'N/A'} />
                        </Grid>
                        <Grid item xs={5} lg={4} sx={{ paddingTop: '5px' }}>
                          <Muitypo sx={{ fontSize: '12px', fontWeight: '400', color: '#3e3e3e', lineHeight: '2' }}>
                            Customer Name
                          </Muitypo>
                        </Grid>
                        <Grid item xs={7} lg={8} sx={{ paddingTop: '5px' }}>
                          <Muitypo variant="caption">{customerDisplayName()}</Muitypo>
                        </Grid>
                        <Grid item xs={5} lg={4} sx={{ paddingTop: '5px' }}>
                          <Muitypo sx={{ fontSize: '12px', fontWeight: '400', color: '#3e3e3e', lineHeight: '2' }}>
                            Order Number
                          </Muitypo>
                        </Grid>
                        <Grid item xs={7} lg={8} sx={{ paddingTop: '5px' }}>
                          <Muitypo variant="caption">{customerVehicle?.orderNumber || 'N/A'}</Muitypo>
                        </Grid>
                      </Grid>
                    </Stack>
                  </TableCell>
                  <TableCell
                    sx={{
                      verticalAlign: 'top',
                      borderLeft: '1px solid #E3E3E3',
                      borderBottom: '1px solid #E3E3E3',
                      p: (theme) => theme.spacing(3.6, 4.5, 3, 4.5),
                      width: '38%',
                    }}
                  >
                    <Muitypo
                      variant="h4"
                      sx={{
                        fontSize: '20px',
                        fontWeight: '500',
                        color: '#1C1951',
                        textAlign: 'center',
                        lineHeight: '24px',
                      }}
                    >
                      Inventory Vehicle
                    </Muitypo>
                    <Stack display="flex" alignItems="center" justifyContent="center" mt={5}>
                      <Box marginBottom={5}>
                        {inventoryVehicle.imageUrl ? (
                          <img src={inventoryVehicle.imageUrl} alt="" width="300px" />
                        ) : (
                          <Box width={300} height={225} boxShadow={1}>
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              paddingTop="22%"
                              paddingLeft="4%"
                              lineHeight={1.4}
                              minWidth={150}
                              fontSize={12}
                              color="gray"
                              size={16}
                              weight={400}
                            >
                              Inventory Vehicle image will be display once it is available
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={5} lg={4} sx={{ paddingTop: '5px' }}>
                          <Muitypo sx={{ fontSize: '12px', fontWeight: '400', color: '#3e3e3e', lineHeight: '2' }}>
                            Stage
                          </Muitypo>
                        </Grid>
                        <Grid item xs={7} lg={8} sx={{ paddingTop: '5px' }}>
                          <Chip text={inventoryVehicle.vehicleStage || 'N/A'} />
                        </Grid>
                        <Grid item xs={5} lg={4} sx={{ paddingTop: '5px' }}>
                          <Muitypo sx={{ fontSize: '12px', fontWeight: '400', color: '#3e3e3e', lineHeight: '2' }}>
                            Batch Number
                          </Muitypo>
                        </Grid>
                        <Grid item xs={7} lg={8} sx={{ paddingTop: '5px' }}>
                          <Muitypo variant="caption">{inventoryVehicle.batchNumber || 'N/A'}</Muitypo>
                        </Grid>
                        <Grid item xs={5} lg={4} sx={{ paddingTop: '5px' }}>
                          <Muitypo sx={{ fontSize: '12px', fontWeight: '400', color: '#3e3e3e', lineHeight: '2' }}>
                            VIN
                          </Muitypo>
                        </Grid>
                        <Grid item xs={7} lg={8} sx={{ paddingTop: '5px' }}>
                          <Muitypo variant="caption">{inventoryVehicle.vin || 'N/A'}</Muitypo>
                        </Grid>
                      </Grid>
                    </Stack>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {propertyToVal?.map((ent, idx) => {
                  let entCurrency: any = 'USD';
                  if (ent.colNameKey === 'totalPrice') {
                    entCurrency = propertyToVal.find((prp) => prp.colNameKey === 'locationCurrency');
                  }
                  if (
                    ['lucidRearSeating', 'dynamicHandlingPackage', 'technologyPackage', 'towingPackage'].includes(
                      ent.colNameKey
                    )
                  ) {
                    const carModelInfo = propertyToVal.find((prp) => prp?.colNameKey === 'carModel');
                    if (!carModelInfo?.inventoryVeh.toLowerCase().includes('gravity')) {
                      return null;
                    }
                  }
                  if (!Object.keys(keyToCellHeading).includes(ent.colNameKey)) {
                    return null;
                  }
                  return (
                    <TableRow key={idx}>
                      <TableCell sx={{ borderRight: '1px solid #E3E3E3', borderBottom: '0', padding: '10px 15px' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box display="flex" justifyContent="flex-start">
                            <Muitypo sx={{ fontSize: '14px', fontWeight: '500', color: '#717171' }}>
                              {keyToCellHeading[ent.colNameKey as keyof typeof keyToCellHeading]}
                            </Muitypo>
                          </Box>
                          <Box display="flex" justifyContent="flex-end">
                            {ent.customerVeh !== ent.inventoryVeh && showDifferences && (
                              <Icon icon="ri:information-line" fontSize={14} color="#E04732" />
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ borderRight: '1px solid #E3E3E3', borderBottom: '0', padding: '10px 15px' }}
                      >
                        <Muitypo
                          sx={{
                            fontSize: '14px',
                            fontWeight: '500',
                            color: ent.customerVeh !== ent.inventoryVeh && showDifferences ? '#ea5948' : '#1F1F1F',
                          }}
                        >
                          {ent.colNameKey === 'totalPrice'
                            ? currencyFormatter(+ent.customerVeh, entCurrency.customerVeh)
                            : ent.customerVeh}
                        </Muitypo>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ borderRight: '1px solid #E3E3E3', borderBottom: '0', padding: '10px 15px' }}
                      >
                        <Muitypo
                          sx={{
                            fontSize: '14px',
                            fontWeight: '500',
                            color: ent.customerVeh !== ent.inventoryVeh && showDifferences ? '#ea5948' : '#1F1F1F',
                          }}
                        >
                          {ent.colNameKey === 'totalPrice'
                            ? currencyFormatter(+ent.inventoryVeh, entCurrency.inventoryVeh)
                            : ent.inventoryVeh}
                        </Muitypo>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </>
  );
};

export default VehicleCompare;
